import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { db, storage } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  Timestamp,
} from "firebase/firestore";
import { listAll, ref, deleteObject } from "firebase/storage";
import dayjs from "dayjs";
import EventList from "../components/EventList";
import EventForm from "../components/EventForm";
import { nanoid } from "nanoid";
import { QRCodeSVG } from "qrcode.react";
import { getEventPublicCode, slugifyEventValue } from "../utils/eventLookup";

const DEFAULT_DISPLAY_SETTINGS = {
  accentColor: "#ec4899",
  effectIntensity: "normal",
  slideshowLayout: "three-photo",
  qrPosition: "bottom-right",
  messageStyle: "both",
};

const AdminSetupPage = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventStats, setEventStats] = useState(null);
  const [reviewItems, setReviewItems] = useState([]);
  const [form, setForm] = useState({
    title: "",
    welcomeMessage: "",
    date: "",
    bannerUrl: "",
    templateId: "",
    animations: {},
    displaySettings: DEFAULT_DISPLAY_SETTINGS,
  });
  const [saving, setSaving] = useState(false);

  const fetchEvents = async () => {
    if (!user) return;
    const snap = await getDocs(collection(db, "users", user.uid, "events"));
    const data = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
    setEvents(data);
  };

  const handleNewEvent = async () => {
    const shortCode = nanoid(8);
    const newEvent = {
      title: "New Event",
      date: Timestamp.fromDate(new Date()),
      welcomeMessage: "",
      createdAt: new Date(),
      shortCode,
      templateId: "classic-pink",
      customSlug: "",
      animations: {},
      displaySettings: DEFAULT_DISPLAY_SETTINGS,
    };
    const ref = await addDoc(
      collection(db, "users", user.uid, "events"),
      newEvent
    );
    await fetchEvents();
    setSelectedEvent({ ...newEvent, id: ref.id });
    setForm({
      ...newEvent,
      date: dayjs(newEvent.date.toDate()).format("YYYY-MM-DD"),
    });
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setForm({
      title: event.title,
      welcomeMessage: event.welcomeMessage,
      date: dayjs(event.date?.toDate?.()).format("YYYY-MM-DD"),
      bannerUrl: event.bannerUrl || "",
      templateId: event.templateId || "",
      customSlug: event.customSlug || "",
      animations: event.animations || {},
      displaySettings: {
        ...DEFAULT_DISPLAY_SETTINGS,
        ...(event.displaySettings || {}),
      },
    });
  };

  const getPublicCode = (event = selectedEvent) =>
    form.customSlug || getEventPublicCode(event);

  const getPublicUrl = (suffix) =>
    `${window.location.origin}/event/${getPublicCode()}${suffix}`;

  const copyToClipboard = async (value) => {
    await navigator.clipboard.writeText(value);
    alert("Copied to clipboard.");
  };

  const downloadQrCode = () => {
    const svg = document.getElementById("event-qr-download");
    if (!svg) return;

    const serializer = new XMLSerializer();
    const svgText = serializer.serializeToString(svg);
    const image = new Image();
    const svgBlob = new Blob([svgText], {
      type: "image/svg+xml;charset=utf-8",
    });
    const url = URL.createObjectURL(svgBlob);

    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = 900;
      canvas.height = 900;
      const context = canvas.getContext("2d");
      context.fillStyle = "#ffffff";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 70, 70, 760, 760);
      URL.revokeObjectURL(url);

      const link = document.createElement("a");
      link.download = `${slugifyEventValue(form.title || "event")}-qr.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    };

    image.src = url;
  };

  const printQrSign = () => {
    const formUrl = getPublicUrl("/form");
    const svg = document.getElementById("event-qr-download");
    const svgMarkup = svg ? new XMLSerializer().serializeToString(svg) : "";
    const qrDataUrl = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
      svgMarkup
    )}`;
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>${form.title} QR Sign</title>
          <style>
            body { margin: 0; font-family: Arial, sans-serif; text-align: center; }
            .sign { min-height: 100vh; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 48px; }
            h1 { font-size: 48px; margin: 0 0 12px; }
            p { font-size: 24px; color: #555; }
            img { width: 360px; height: 360px; margin: 24px 0; }
            .url { font-size: 18px; word-break: break-all; }
          </style>
        </head>
        <body>
          <main class="sign">
            <h1>Send ${form.title} a message</h1>
            <p>Scan to add a photo or note</p>
            <img alt="QR code" src="${qrDataUrl}" />
            <div class="url">${formUrl}</div>
          </main>
          <script>window.onload = () => window.print();</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const fetchEventStats = async (event = selectedEvent) => {
    if (!user || !event) return;

    const msgSnap = await getDocs(
      collection(db, "users", user.uid, "events", event.id, "messages")
    );
    const messages = msgSnap.docs.map((doc) => doc.data());
    const folderRef = ref(storage, `users/${user.uid}/events/${event.id}/uploads`);
    const uploads = await listAll(folderRef);
    const latestTimestamp = messages
      .map((message) => message.timestamp?.toDate?.())
      .filter(Boolean)
      .sort((a, b) => b - a)[0];

    setEventStats({
      messages: messages.length,
      photos: uploads.items.length,
      needsReview: messages.filter((message) => message.moderationStatus === "needsReview").length,
      latest: latestTimestamp,
    });
  };

  const fetchReviewItems = async (event = selectedEvent) => {
    if (!user || !event) return;

    const msgSnap = await getDocs(
      collection(db, "users", user.uid, "events", event.id, "messages")
    );
    const items = msgSnap.docs
      .map((messageDoc) => ({ id: messageDoc.id, ...messageDoc.data() }))
      .filter((message) => message.moderationStatus === "needsReview");

    setReviewItems(items);
  };

  const refreshEventReview = async (event = selectedEvent) => {
    await Promise.all([fetchEventStats(event), fetchReviewItems(event)]);
  };

  const approveReviewItem = async (messageId) => {
    if (!user || !selectedEvent) return;

    await updateDoc(
      doc(db, "users", user.uid, "events", selectedEvent.id, "messages", messageId),
      {
        moderationStatus: "approved",
        moderationReason: "",
      }
    );

    await refreshEventReview(selectedEvent);
  };

  const deleteReviewItem = async (item) => {
    if (!user || !selectedEvent) return;

    if (item.imageName) {
      await deleteObject(
        ref(storage, `users/${user.uid}/events/${selectedEvent.id}/uploads/${item.imageName}`)
      ).catch((err) => {
        console.warn("Could not delete reviewed image.", err);
      });
    }

    await deleteDoc(
      doc(db, "users", user.uid, "events", selectedEvent.id, "messages", item.id)
    );

    await refreshEventReview(selectedEvent);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!user || !selectedEvent) return;
    setSaving(true);
    const ref = doc(db, "users", user.uid, "events", selectedEvent.id);
    const selectedEventDate = selectedEvent.date?.toDate
      ? selectedEvent.date.toDate()
      : new Date(form.date);
    const canChangeDate =
      dayjs(selectedEventDate).isSame(dayjs(), "day") ||
      dayjs(selectedEventDate).isAfter(dayjs(), "day");
    await updateDoc(ref, {
      ...form,
      customSlug: slugifyEventValue(form.customSlug || ""),
      date: Timestamp.fromDate(
        canChangeDate ? new Date(form.date) : selectedEventDate
      ),
      updatedAt: new Date(),
    });
    setSaving(false);
    alert("Event updated!");
    fetchEvents();
    refreshEventReview(selectedEvent);
  };

  const handleDelete = async () => {
    if (!user || !selectedEvent) return;
    const confirmDelete = window.confirm("Delete this event?");
    if (!confirmDelete) return;

    const folderRef = ref(
      storage,
      `users/${user.uid}/events/${selectedEvent.id}`
    );
    const allFiles = await listAll(folderRef);
    await Promise.all(allFiles.items.map((fileRef) => deleteObject(fileRef)));

    await deleteDoc(doc(db, "users", user.uid, "events", selectedEvent.id));
    setSelectedEvent(null);
    fetchEvents();
  };

  useEffect(() => {
    fetchEvents();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (selectedEvent) {
      refreshEventReview(selectedEvent);
    } else {
      setEventStats(null);
      setReviewItems([]);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEvent?.id]);

  const eventDate = selectedEvent?.date?.toDate
    ? dayjs(selectedEvent.date.toDate())
    : null;
  const isEditable =
    eventDate &&
    (eventDate.isSame(dayjs(), "day") || eventDate.isAfter(dayjs(), "day"));
  const guestSubmissionsOpen = isEditable;
  const lockEventDate = !guestSubmissionsOpen;

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-gray-50">
      {/* Left */}
      <div className="w-full lg:w-1/3 bg-white p-4 sm:p-6 border-b lg:border-r border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h2 className="sticky text-xl font-bold text-pink-600">
            Your Events
          </h2>
          <button
            onClick={handleNewEvent}
            className="bg-pink-500 hover:bg-pink-600 text-white px-3 py-1 rounded text-sm"
          >
            + New Event
          </button>
        </div>
        <EventList
          events={events}
          selectedId={selectedEvent?.id}
          onSelect={handleEventClick}
        />
      </div>

      {/* Right */}
      <div className="w-full lg:w-2/3 p-4 sm:p-8">
        {selectedEvent ? (
          <>
            {!guestSubmissionsOpen && (
              <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                This event has passed. Organiser tools stay available, but the
                public guest form is closed for new messages and photos.
              </div>
            )}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  Public Page URL
                </label>
                <a
                  href={getPublicUrl("/welcome")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline break-all"
                >
                  {getPublicUrl("/welcome")}
                </a>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  Collage Page URL
                </label>
                <a
                  href={getPublicUrl("/collage")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline break-all"
                >
                  {getPublicUrl("/collage")}
                </a>
              </div>

              <div className="mb-6 rounded-lg border border-pink-100 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">
                      Share & Event Tools
                    </h3>
                    <p className="mt-1 break-all text-sm text-gray-600">
                      Guest form: {getPublicUrl("/form")}
                    </p>
                    {eventStats && (
                      <div className="mt-3 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                        <div className="rounded bg-pink-50 p-2">
                          <strong>{eventStats.messages}</strong>
                          <span className="block text-gray-500">Messages</span>
                        </div>
                        <div className="rounded bg-pink-50 p-2">
                          <strong>{eventStats.photos}</strong>
                          <span className="block text-gray-500">Photos</span>
                        </div>
                        <div className="rounded bg-pink-50 p-2">
                          <strong>{eventStats.needsReview}</strong>
                          <span className="block text-gray-500">Review</span>
                        </div>
                        <div className="rounded bg-pink-50 p-2">
                          <strong>
                            {eventStats.latest
                              ? dayjs(eventStats.latest).format("HH:mm")
                              : "-"}
                          </strong>
                          <span className="block text-gray-500">Latest</span>
                        </div>
                      </div>
                    )}
                  </div>
                  <QRCodeSVG
                    id="event-qr-download"
                    value={getPublicUrl("/form")}
                    size={112}
                    className="shrink-0 rounded bg-white p-1"
                  />
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2 lg:grid-cols-4">
                  <button type="button" onClick={() => copyToClipboard(getPublicUrl("/form"))} className="rounded bg-pink-500 px-3 py-2 text-sm font-semibold text-white">
                    Copy Form Link
                  </button>
                  <button type="button" onClick={downloadQrCode} className="rounded bg-white px-3 py-2 text-sm font-semibold text-pink-600 ring-1 ring-pink-200">
                    Download QR
                  </button>
                  <button type="button" onClick={printQrSign} className="rounded bg-white px-3 py-2 text-sm font-semibold text-pink-600 ring-1 ring-pink-200">
                    Print QR Sign
                  </button>
                  <button type="button" onClick={() => refreshEventReview()} className="rounded bg-white px-3 py-2 text-sm font-semibold text-pink-600 ring-1 ring-pink-200">
                    Refresh Stats
                  </button>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <a href={getPublicUrl("/welcome")} target="_blank" rel="noopener noreferrer" className="rounded bg-gray-100 px-3 py-2 text-center text-sm font-semibold text-gray-700">
                    Open Slideshow
                  </a>
                  <a href={getPublicUrl("/collage")} target="_blank" rel="noopener noreferrer" className="rounded bg-gray-100 px-3 py-2 text-center text-sm font-semibold text-gray-700">
                    Open Collage
                  </a>
                </div>
              </div>

              {reviewItems.length > 0 && (
                <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 shadow-sm">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-lg font-bold text-amber-900">
                        Moderation Review
                      </h3>
                      <p className="text-sm text-amber-800">
                        These guest submissions are hidden until you approve them.
                      </p>
                    </div>
                    <span className="rounded-full bg-amber-200 px-3 py-1 text-sm font-bold text-amber-900">
                      {reviewItems.length}
                    </span>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    {reviewItems.map((item) => (
                      <div key={item.id} className="rounded-lg bg-white p-3 shadow-sm">
                        {item.imageUrl && (
                          <img
                            src={item.imageUrl}
                            alt="Pending review"
                            className="mb-3 h-40 w-full rounded object-cover"
                          />
                        )}
                        <p className="text-sm italic text-gray-700">
                          "{item.message}"
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          - {item.name || "Anonymous"} ·{" "}
                          {item.moderationReason || "needs review"}
                        </p>
                        <div className="mt-3 flex gap-2">
                          <button
                            type="button"
                            onClick={() => approveReviewItem(item.id)}
                            className="rounded bg-green-600 px-3 py-2 text-sm font-semibold text-white"
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteReviewItem(item)}
                            className="rounded bg-red-600 px-3 py-2 text-sm font-semibold text-white"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <EventForm
                form={form}
                setForm={setForm}
                onSubmit={handleUpdate}
                onDelete={handleDelete}
                saving={saving}
                user={user}
                eventId={selectedEvent.id}
                lockDate={lockEventDate}
              />
            </>
        ) : (
          <p className="text-gray-500 text-lg">
            Select an event to view or edit.
          </p>
        )}
      </div>
    </div>
  );
};

export default AdminSetupPage;
